import { IDog, IProfile } from '@jumpstart/core';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { addDoc, collection, deleteDoc, QueryDocumentSnapshot, QuerySnapshot } from 'firebase/firestore';
import { Form, Formik } from 'formik';
import moment from 'moment';
import React, { useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import * as yup from 'yup';
import { date, object, string } from 'yup';
import { db } from '../../../../core/firebase';
import { ISubscriber } from '../../schemas';

export interface IProfileTasksView {
  subscriber: ISubscriber | null;
  dog: IDog | null;
  profile: IProfile | null;
  tasks: QuerySnapshot | null;
}

export const taskSchema = object().shape({
  text: string()
    .required('Task content is required'),
  hasDate: yup.boolean(),
  endDate: date()
    .nullable()
    .when('optIn', {
      is: true,
      then: yup.string().required('Must enter a date')
    })
});

function TaskListItem(props: { task: QueryDocumentSnapshot; handleDeleteTask: (task: QueryDocumentSnapshot) => void }) {
  const { task, handleDeleteTask } = props;
  const [copiedText, setCopiedText] = useState('');
  return (
    <CopyToClipboard
      text={task.get('text')}
      onCopy={() => setCopiedText(task.get('text'))}
    >
      <Tooltip
        title={
          copiedText === task.get('text')
            ? 'This was Copied!'
            : 'Copy To Clipboard'
        }
        placement="top"
      >
        <ListItem button>
          <ListItemText
            primary={task.get('text')}
            style={{whiteSpace: 'pre-line'}}
            secondary={task.get('endDate') ? `Ends ${moment.duration(
              moment(task.get('endDate')).diff(moment(), 'days'),
              'days'
            ).humanize(true)}` : ''}
          />
          <ListItemSecondaryAction>
            <IconButton onClick={() => handleDeleteTask(task)} size="small">
              <CloseIcon color="error"/>
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>
      </Tooltip>
    </CopyToClipboard>
  );
}

export default function ProfileNotesView(props: IProfileTasksView) {
  const { profile, dog, subscriber, tasks } = props;
  const [open, setOpen] = React.useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [staged, setStaged] = useState<QueryDocumentSnapshot | null>(null);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDeleteTask = (task: QueryDocumentSnapshot | null) => {
    if (!task || !subscriber) {
      return;
    }
    deleteDoc(task.ref)
      .then(() => {
        console.log('Deleted Task');
      })
      .catch((err: Error) => {
        console.error('Failed to delete task', err);
      });
  };


  const handleAddTask = async (values: { text: string; endDate: moment.Moment | null; hasDate: boolean }) => {
    console.log('ADDING TASK', values);
    setSubmitting(true);
    if (!subscriber) {
      console.error('No subscriber');
      return;
    }
    addDoc(collection(db, "scout9-sms-subscribers", subscriber.uid, 'tasks'), {
      text: values.text,
      endDate: values.hasDate ? values.endDate ? values.endDate.toISOString() : null : null
    })
      .then((res) => {
        console.log('Added', res.path);
      })
      .catch((err: Error) => {
        console.error('Failed to add task', err);
      });
    setSubmitting(false);
    setOpen(false);
  };


  if (!dog || !profile || !subscriber) {
    return null;
  }

  return (
    <Grid direction="column" container wrap="nowrap">
      <List dense>
        {
          tasks ? tasks.docs.map((task: QueryDocumentSnapshot) => (
            <TaskListItem task={task} handleDeleteTask={handleDeleteTask} key={task.id}/>
          )) : null
        }
        <ListItem button onClick={handleClickOpen}>
          Add Task
        </ListItem>
      </List>

      <Dialog open={!!staged} onClose={() => {
        setStaged(null);
      }}>
        <DialogTitle>New Task</DialogTitle>
        <DialogContent>Complete task?</DialogContent>

        <Button variant="contained" type="submit" onClick={() => setStaged(null)}>
          No
        </Button>
        <Button variant="contained" type="submit" color="primary" onClick={() => handleDeleteTask(staged)}>
          Yes
        </Button>
      </Dialog>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>New Task</DialogTitle>
        <DialogActions
          style={{ width: '100%', position: 'absolute' }}
        >
          <IconButton onClick={handleClose}><CloseIcon/></IconButton>
        </DialogActions>
        <DialogContent>
          <Formik
            initialValues={{
              text: '',
              endDate: moment().add(1, 'week'),
              hasDate: false
            }}
            onSubmit={handleAddTask}
            validationSchema={taskSchema}
          >
            {({ values, setFieldValue, handleChange, submitCount, errors, touched, handleBlur }) => (
              <Form>

                <TextField
                  margin="dense"
                  id="text"
                  name="text"
                  label="Task"
                  fullWidth
                  multiline
                  value={values.text}
                  onChange={handleChange}
                  error={(touched.text || submitCount > 0) && Boolean(errors.text)}
                  helperText={(touched.text || submitCount > 0) && errors.text ? errors.text : null}
                  variant="outlined"
                  placeholder="Task info"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      id="hasDate"
                      name="hasDate"
                      checked={values.hasDate}
                      onChange={value => setFieldValue('hasDate', value.target.checked)}
                      color="primary"
                    />
                  }
                  label="Timed End Date"
                />

                <Collapse in={values.hasDate}>
                  <TextField
                    name="endDate"
                    label="End Date"
                    variant="outlined"
                    value={values.endDate}
                    onChange={value => setFieldValue('endDate', value)}
                    type="datetime-local"
                  />
                </Collapse>
                <Button variant="contained" type="submit" color="secondary">
                  Submit {submitting ? <CircularProgress style={{ color: '#1F2D3D' }} size={20}/> : null}
                </Button>

              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

    </Grid>
  );
}
